import { type PropsWithChildren, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import { api } from '../../utils/data-utils';
import Error404Screen from '../ErrorScreen/Error404Screen';

export default function ScsGuidance({
  inline = false,
  children,
}: PropsWithChildren<{ inline?: boolean }> = {}) {
  const { tenant } = useParams();
  const { fetchJWT } = useAuthContext();
  const [status, setStatus] = useState(inline ? '' : 'Preparing download...');
  const [error, setError] = useState('');
  const [requested, setRequested] = useState(false);
  useEffect(() => {
    if (tenant !== 'scs' || (inline && !requested)) return;
    let cancelled = false;
    const download = async () => {
      try {
        const token = (await fetchJWT(inline ? { redirectOnFail: false } : undefined)).idToken;
        if (!token) throw new Error('Not authenticated');
        const response = await api(
          token,
          '/form',
          { cache: 'no-store' },
          { document: 'scs-scope' },
        );
        const document = await response.json();
        if (
          typeof document.contentBase64 !== 'string' ||
          typeof document.filename !== 'string' ||
          typeof document.contentType !== 'string'
        ) {
          throw new Error('Invalid document response');
        }
        const bytes = Uint8Array.from(atob(document.contentBase64), (character) =>
          character.charCodeAt(0),
        );
        if (cancelled) return;
        const url = URL.createObjectURL(new Blob([bytes], { type: document.contentType }));
        const link = window.document.createElement('a');
        link.href = url;
        link.download = document.filename;
        window.document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setStatus(
          inline
            ? 'Your document has been downloaded.'
            : 'Your document has been downloaded. You can close this tab.',
        );
      } catch (cause) {
        if (!cancelled)
          setError(cause instanceof Error ? cause.message : 'Unable to download document.');
      } finally {
        if (!cancelled) setRequested(false);
      }
    };
    void download();
    return () => {
      cancelled = true;
    };
  }, [tenant, fetchJWT, inline, requested]);
  if (inline) {
    return (
      <span>
        <button
          type="button"
          className="govuk-link"
          style={{ border: 0, padding: 0, background: 'none', font: 'inherit', cursor: 'pointer' }}
          disabled={requested || tenant !== 'scs'}
          onClick={() => {
            setError('');
            setStatus('Preparing download...');
            setRequested(true);
          }}
        >
          {children}
        </button>
        {error ? (
          <span className="govuk-error-message" role="alert">
            {error}
          </span>
        ) : (
          <span className="govuk-visually-hidden" role="status">
            {status}
          </span>
        )}
      </span>
    );
  }
  if (tenant !== 'scs') return <Error404Screen />;
  return (
    <main className="govuk-width-container govuk-main-wrapper">
      <h1 className="govuk-heading-l">SCS scope guidance</h1>
      {error ? (
        <p className="govuk-error-message" role="alert">
          {error}
        </p>
      ) : (
        <p className="govuk-body" role="status">
          {status}
        </p>
      )}
      <a className="govuk-link" href="/scs/cover-sheet/cover-sheet">
        Return to cover sheet
      </a>
    </main>
  );
}
